import React from 'react';
import { Link } from 'react-router-dom';
import { Lightbulb } from 'lucide-react';
import './ProgramsPage.css';

const ProgramsPage = () => {
  return (
    <div className="programs-page">
      {/* Hero Section */}
      <section className="page-hero programs-hero" data-testid="programs-hero">
        <div className="hero-with-ladders">
          <img 
            src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/kc5vzhwq_laddergirltr.png"
            alt="Learning journey"
            className="hero-ladder hero-ladder-left"
          />
          
          <div className="page-hero-content">
            <div className="hero-icon-wrapper">
              <Lightbulb size={80} color="white" className="hero-icon" />
            </div>
            <h1>Our Programs</h1>
            <p>12-week programs building practical and critical skills for a sustainable future</p>
          </div>
          
          <img 
            src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/t4srckf6_ladderboytr.png"
            alt="Learning journey"
            className="hero-ladder hero-ladder-right"
          />
        </div>
      </section>

      {/* For a Sustainable World Section */}
      <section className="section sdg-mission-section">
        <div className="content-wrapper">
          <h2 className="section-title">For a Sustainable World</h2>
          <p className="intro-text">
            Our mission is to empower future generations to create a fairer, more sustainable world. 
            We offer a <strong>12-week program series</strong> featuring sustainability courses conducted 
            by <strong>partner university professors</strong>. Guided by the <strong>United Nations Sustainable 
            Development Goals (SDGs)</strong>, we integrate all 17 SDGs into our programs, focusing on 
            advancing <strong>quality education</strong>, fostering <strong>sustainable communities</strong>, 
            and promoting <strong>responsible production and consumption</strong> as key pillars.
          </p>
          <div className="sdg-cta">
            <Link to="/programs#programs-list" className="btn btn-secondary">
              View Program List
            </Link>
          </div>
        </div>
      </section>

      {/* Holistic Approach Section */}
      <section className="section">
        <div className="content-wrapper">
          <h2 className="section-title">Our Holistic Approach</h2>
          <p className="intro-text">
            We are dedicated to providing education that extends beyond language skills. Our focus is on 
            empowering students with the knowledge and practices necessary for sustainable living, preparing 
            them for a brighter future.
          </p>
        </div>
      </section>

      {/* Our Commitment Section */}
      <section className="section" style={{ backgroundColor: 'var(--light-grey)' }}>
        <div className="content-wrapper">
          <h2 className="section-title">Our Commitment</h2>
          <p className="intro-text">
            The program content is even more relevant given how rapidly things are shifting around us. Some of 
            these core topics, such as basic human needs, sustainability, survival skills, and understanding 
            global crises, go beyond traditional education. These are all practical, critical skills that kids 
            are definitely not getting enough of in a standard curriculum.
          </p>
          <p className="intro-text">
            Our 12-week programs for 12-18-year-olds focus on building practical and critical skills for a 
            sustainable future. Each program offers hands-on learning via self-learning, engaging discussions, 
            creative problem-solving, and real-world applications.
          </p>
        </div>
      </section>

      {/* Impact Image Section */}
      <section className="section impact-image-section">
        <div className="content-wrapper">
          <div className="impact-image-container">
            <img 
              src="https://customer-assets.emergentagent.com/job_spec-evaluator/artifacts/mlhqzux5_hh.png" 
              alt="Students celebrating their achievements with Litvill founders" 
              className="impact-image"
            />
            <p className="image-caption">
              Our students celebrating their achievements - proof that education transforms lives
            </p>
          </div>
        </div>
      </section>

      {/* Published Works Section */}
      <section className="section published-works-section" style={{ backgroundColor: 'var(--light-grey)' }}>
        <div className="content-wrapper">
          <h2 className="section-title">Our Published Work: Student Voices on Sustainability</h2>
          
          <div className="book-showcase">
            <div className="book-cover-container">
              <img 
                src="https://customer-assets.emergentagent.com/job_spec-evaluator/artifacts/84pbmwx8_cover_front.png" 
                alt="Fundamentals of Life: Water - Book Cover" 
                className="book-cover"
              />
            </div>
            
            <div className="book-description">
              <p className="book-quote">"Every great change begins with a small step, and this book is full of them."</p>
              
              <p className="book-text">
                This publication celebrates the work of students from our <em>Fundamentals of Life</em> program - 
                a three-month journey exploring water, food, air, and fire through real-world challenges. Starting 
                with water, students examined not just its essential role in life, but how it's disappearing, 
                becoming polluted, and often taken for granted.
              </p>
              
              <p className="book-text">
                What makes this book special isn't just the topic - it's the students behind it. They didn't just 
                complete tasks. They explored, reflected, and cared. These young minds leaned in with curiosity, 
                honesty, and effort, bringing their experiences, creativity, and hope to address challenges many 
                adults turn away from.
              </p>
              
              <p className="book-text">
                This collaborative effort between Litvill Lessons Association, Chitkara University, and Harmony 
                House India showcases the best work from 29 students who chose to think critically about the 
                systems that keep life going - and how to protect them.
              </p>
              
              <p className="book-emphasis">This is just the beginning.</p>
              
              <div className="book-actions">
                <a 
                  href="https://www.flipsnack.com/7DDC959BDC9/water" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn btn-primary"
                  data-testid="view-book-btn"
                >
                  Read Online
                </a>
                <Link to="/contact" className="btn btn-secondary" data-testid="request-book-btn">
                  Request Free Hard Copy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="section" id="programs-list">
        <div className="content-wrapper">
          <h2 className="section-title">Our Programs</h2>
          
          <div className="programs-list">
            {/* Program 1 */}
            <div className="program-detail-card" data-testid="program-fundamentals">
              <h3>Fundamentals of Life</h3>
              <h4>A Practical Guide to Nature's Building Blocks</h4>
              <p>
                Learn how to find and purify water, assess air quality, and understand fire's role in both 
                survival and destruction. Dive into food systems by identifying ways to reduce waste, promote 
                local, seasonal, and organic options, and propose community solutions to hunger. This program 
                combines hands-on activities and practical examples to tackle real-world challenges, empowering 
                you to make sustainable choices and drive meaningful change.
              </p>
            </div>

            {/* Program 2 */}
            <div className="program-detail-card" data-testid="program-survival">
              <h3>Survival Skills</h3>
              <h4>Thriving in the Face of Uncertainty</h4>
              <p>
                Explore the critical crises impacting our planet: ecosystem collapse, shrinking natural spaces, 
                water scarcity, the energy crisis, climate change, and pollution. Through in-depth analysis and 
                creative problem-solving, you will brainstorm innovative strategies for a sustainable future. 
                This program builds understanding and resilience, preparing participants to take on these urgent 
                issues with informed, actionable solutions and a commitment to positive change.
              </p>
            </div>

            {/* Program 3 */}
            <div className="program-detail-card" data-testid="program-earth">
              <h3>Earth Under Pressure</h3>
              <h4>Navigating Crises Together</h4>
              <p>
                Explore the critical crises impacting our planet: ecosystem collapse, shrinking natural spaces, 
                water scarcity, the energy crisis, climate change, and pollution. Through in-depth analysis and 
                creative problem-solving, you will brainstorm innovative strategies for a sustainable future. 
                This program builds understanding and resilience, preparing participants to take on these urgent 
                issues with informed, actionable solutions and a commitment to positive change.
              </p>
            </div>

            {/* Program 4 */}
            <div className="program-detail-card" data-testid="program-future">
              <h3>Future Forward</h3>
              <h4>Careers in Tomorrow's World</h4>
              <p>
                Prepare to thrive in a rapidly evolving job market shaped by AI, technological advances, and a 
                growing emphasis on sustainability and social responsibility. Investigate and identify emerging 
                job trends and essential skills for future careers in AI-driven industries, sustainable 
                development, and social impact. Analyze the key skills needed to succeed in this evolving market. 
                Reflect on personal goals and interests to align with the dynamic opportunities of the future 
                job market.
              </p>
            </div>

            {/* Program 5 */}
            <div className="program-detail-card" data-testid="program-truth">
              <h3>Truth Quest</h3>
              <h4>Decoding Information in a Digital Age</h4>
              <p>
                Our ability to distinguish AI-generated content from reality is fading, if it was ever strong 
                at all. We're immersed in a digital sea increasingly filled with low-quality information, spam, 
                deepfakes, partial truths, and outright disinformation. Learn to differentiate between truth and 
                propaganda by exploring logic, statistics, and effective research techniques. Develop critical 
                thinking skills to assess sources and make informed decisions. Evaluate online sources for 
                credibility and identify reliable information.
              </p>
            </div>

            {/* Program 6 */}
            <div className="program-detail-card" data-testid="program-world">
              <h3>World Dynamics</h3>
              <h4>Global Conflicts and Human Rights</h4>
              <p>
                Discover how politics, economics, and global conflicts intertwine while exploring the critical 
                role of human rights and institutions. Engage in meaningful discussions on historical events and 
                current affairs, analyzing how these forces shape societies and impact daily life. Through 
                interactive simulations and case studies, you'll learn to advocate for human rights and 
                understand how institutions influence global issues.
              </p>
            </div>

            {/* Program 7 */}
            <div className="program-detail-card" data-testid="program-understanding">
              <h3>The Power of Understanding</h3>
              <h4>Connecting with Others</h4>
              <p>
                Develop self-awareness and empathy through engaging discussions and activities designed to 
                understand different perspectives. Learn effective communication skills and explore the importance 
                of diversity in building a more inclusive society. Work on impactful presentations and group 
                discussions that enhance your ability to connect, persuade, and inspire.
              </p>
            </div>

            {/* Program 8 */}
            <div className="program-detail-card" data-testid="program-wellness">
              <h3>Wellness Warriors</h3>
              <h4>Living a Long, Healthy Life</h4>
              <p>
                Learn how to nurture both your mind and body for a balanced, fulfilling life. Through engaging 
                lessons and activities, we guide you in exploring holistic approaches to well-being, including 
                nutrition, exercise, mindfulness, and self-care practices.
              </p>
            </div>

            {/* Program 9 */}
            <div className="program-detail-card" data-testid="program-leadership">
              <h3>Leadership in Sustainability</h3>
              <h4>From Ideas to Action</h4>
              <p>
                Become a leader for a sustainable future by learning the skills and qualities needed to drive 
                change. Explore the traits of effective leaders, like resilience, adaptability, empathy, and 
                vision, while reflecting on your own strengths and areas for growth. Learn to think holistically 
                with systems thinking, understanding the interconnectedness of environmental, social, and economic 
                factors in tackling sustainability challenges. Develop policy and advocacy skills, mastering 
                public speaking, persuasive writing, and media literacy to create impactful campaigns.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section" style={{ backgroundColor: 'var(--light-grey)' }}>
        <div className="cta-content">
          <h2>Ready to Make a Difference?</h2>
          <p>Join our programs and become part of the solution</p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn btn-primary" data-testid="programs-join">
              Enroll Now
            </Link>
            <Link to="/movement" className="btn btn-secondary" data-testid="programs-movement">
              Explore the Movement
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgramsPage;
